import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { updateUserProfile, getUserRegistrations, updatePaymentStatus } from './firestoreService';
import { useNavigate } from 'react-router-dom';
import './AccountPage.css';

const AccountPage = () => {
  const { user, profile, refreshProfile, logout } = useAuth();
  // Map database workshop names to frontend names
  const mapWorkshopName = (eventName) => {
    const workshopMapping = {
      'AI/ML Workshop': 'Gen AI Workshop: From Prompts to Prototypes',
      'Web Development Workshop': 'Explore Full Stack: The Ultimate Hands-on Workshop',
      'Cybersecurity Workshop': 'Emerging Cyber Threats: Attacks, Trends, and Defense Strategies',
      'Mobile App Development Workshop': 'LLM and Agentic AI: Intelligent Models, Autonomy, and Future Innovations'
    };
    return workshopMapping[eventName] || eventName;
  };
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    college: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ transactionId: '', paymentProof: null });
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Fetch user registrations
  useEffect(() => {
    const fetchRegistrations = async () => {
      if (user) {
        try {
          setLoadingRegistrations(true);
          const userRegistrations = await getUserRegistrations(user.uid);
          setRegistrations(userRegistrations);
        } catch (error) {
          console.error('Error fetching registrations:', error);
        } finally {
          setLoadingRegistrations(false);
        }
      }
    };

    fetchRegistrations();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEditModal = () => {
    const editCount = profile?.editCount || 0;
    if (editCount >= 3) {
      alert('You have reached the maximum number of profile edits (3 times).');
      return;
    }
    
    setEditFormData({
      name: profile?.name || user.displayName || '',
      phone: profile?.phone || '',
      college: profile?.college || ''
    });
    setSaveMessage('');
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSaveMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');

    try {
      const editCount = profile?.editCount || 0;
      
      // Update profile and set status to active when all fields are complete
      const updateData = {
        name: editFormData.name,
        phone: editFormData.phone,
        college: editFormData.college,
        status: 'active', // Mark profile as active/complete
        profileComplete: true,
        editCount: editCount + 1
      };

      await updateUserProfile(user.uid, updateData);

      setSaveMessage('Profile updated successfully!');
      
      // Refresh profile data from database
      await refreshProfile();
      
      setTimeout(() => {
        closeEditModal();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveMessage('Error updating profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.transactionId || !paymentForm.paymentProof) {
      alert('Please provide both transaction ID and payment proof.');
      return;
    }

    setIsSubmittingPayment(true);
    try {
      await updatePaymentStatus(user.uid, paymentForm.transactionId, paymentForm.paymentProof);
      alert('Payment submitted successfully! Verification may take up to 24 hours.');
      setShowPaymentModal(false);
      await refreshProfile();
    } catch (error) {
      console.error('Payment submission error:', error);
      alert('Failed to submit payment. Please try again.');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  if (!user) {
    return (
      <div className="account-container">
        <div className="account-card">
          <h2>Please login to view your profile</h2>
        </div>
      </div>
    );
  }

  return (
    <section className="account-container" id="account">
    <div className="account-header">
        <h1> </h1>
    </div>
      <div className="dashboard-wrapper">
        {/* Left Side - User Profile Card */}
        <div className="user-profile-section">
          <div className="profile-card">
            <div className="profile-top">
              <div className="profile-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="profile-name">
                <h2>{profile?.name || user.displayName || 'User'}</h2>
                <p className="role">Verified Student</p>
              </div>
            </div>

            <div className="profile-details">
              <div className="detail-item">
                <label>Email Address</label>
                <p>{user.email}</p>
                <i className="fas fa-envelope"></i>
              </div>

              <div className="detail-item">
                <label>Mobile Number</label>
                <p>{profile?.phone || 'Not provided'}</p>
                <i className="fas fa-phone"></i>
              </div>

              <div className="detail-item">
                <label>College / Institution</label>
                <p>{profile?.college || 'Not provided'}</p>
                <i className="fas fa-building"></i>
              </div>

              <div className="detail-row">
                <div className="detail-item">
                  <label>Last Update</label>
                  <p>{profile?.updatedAt ? new Date(profile.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Member Id</label>
                  <p>{profile?.memberID || 'Loading...'}</p>
                </div>
              </div>
              

            </div>

            <div className="profile-actions">
              <button 
                onClick={openEditModal} 
                className="edit-btn"
                disabled={(profile?.editCount || 0) >= 3}
              >
                <i className="fas fa-edit"></i>
                Edit Profile
              </button>
              <button onClick={handleLogout} className="logout-btn">
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </button>
            </div>

            {/* Payment Section */}
            {profile?.status === 'active' && (
              <div className="payment-section">
                <h3>Payment Status</h3>
                {profile?.pay_status_both ? (
                  <div className="payment-status paid">
                    <i className="fas fa-check-circle"></i>
                    Pay Status Confirmed
                  </div>
                ) : profile?.pay_status_tech_only ? (
                  <div className="payment-status paid">
                    <i className="fas fa-check-circle"></i>
                    Pay Status Tech Only
                  </div>
                ) : (
                  <div className="payment-status pending">
                    <i className="fas fa-clock"></i>
                    Payment Pending
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Events Section */}
        <div className="events-section">
          {/* Register for Events Card */}
          
          {/* My Registrations */}
          <div className="registrations-section">
            <h3>
              <i className="fas fa-list-check"></i>
              My Registrations
            </h3>

            {loadingRegistrations ? (
              <div className="loading-registrations">
                <p>Loading registrations...</p>
              </div>
            ) : (1) ? (
              <>
               {(profile?.pay_status_both || profile?.pay_status_tech_only) && 
                <div className="registration-item cyan">
                  <div className="reg-header">
                    <h4>{profile?.pay_status_both ? 'Ultimate Pass(TECH + NON TECH)' : 'Technical Pass'}</h4>
                    <span className="status confirmed">Confirmed</span>
                  </div>
                  
                  <div className="reg-details">
                    <p><i className="fas fa-calendar"></i> Registered</p>
                    <p><i className="fas fa-check-circle"></i> Payment Verified</p>
                  </div>
                </div>
                }
                {profile?.registeredEvents && Array.isArray(profile.registeredEvents) && profile.registeredEvents.length > 0 ? (
                  profile.registeredEvents.map((eventName, index) => (
                    <div key={index} className={`registration-item ${['blue', 'pink', 'purple'][index % 3]}`}>
                      <div className="reg-header">
                        <h4>{mapWorkshopName(eventName)}</h4>
                        <span className="status confirmed">Confirmed</span>
                      </div>
                      <div className="reg-details">
                        <p><i className="fas fa-calendar"></i> Registered</p>
                      </div>
                    </div>
                  ))
                ) : null}
              </>
            ) : (
              <div className="no-registrations">
                <p>No event registered yet 😔</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="close-modal" onClick={closeEditModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleSaveProfile} className="edit-form">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Mobile Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your mobile number"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="college">College / Institution</label>
                <input
                  type="text"
                  id="college"
                  name="college"
                  value={editFormData.college}
                  onChange={handleInputChange}
                  placeholder="Enter your college name"
                />
              </div>

              {saveMessage && (
                <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
                  {saveMessage}
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={closeEditModal} 
                  className="cancel-btn"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Payment - ₹250</h2>
              <button className="close-modal" onClick={() => setShowPaymentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="qr-section">
              <img src="/images/qr-placeholder.png" alt="Payment QR Code" className="qr-code" />
              <p>Scan QR code to pay ₹250</p>
            </div>
            
            <form onSubmit={handlePaymentSubmit} className="payment-form">
              <div className="form-group">
                <label htmlFor="transactionId">Transaction ID</label>
                <input
                  type="text"
                  id="transactionId"
                  value={paymentForm.transactionId}
                  onChange={(e) => setPaymentForm({...paymentForm, transactionId: e.target.value})}
                  placeholder="Enter transaction ID"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentProof">Payment Proof</label>
                <input
                  type="file"
                  id="paymentProof"
                  accept="image/*,.pdf"
                  onChange={(e) => setPaymentForm({...paymentForm, paymentProof: e.target.files[0]})}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="cancel-btn" disabled={isSubmittingPayment}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={isSubmittingPayment}>
                  {isSubmittingPayment ? 'Submitting...' : 'Submit Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default AccountPage;